import { GatsbyButton } from '@lp-root/src/components/atm.button/button.component';
import {
  Anxiety,
  Concerned,
  ControlConcerns,
  Discouragement,
  Gender,
  InitialInsomnia,
  Interference,
  MiddleInsomnia,
  OthersPerception,
  Pleasure,
  QuizB2BData,
  Satisfaction,
  SleepDisturb,
  SleepProblemAge,
  TerminalInsomnia,
  WeekFrequency,
} from '@lp-root/src/data/quiz-typeform.model';
import { BigInputLabel, Body, Col, FaIcon, FeaturedH2, H1, H3, Row, Separator } from '@web/atomic';
import { RadioField } from '@web/atomic/legacy/atm.radio';
import { TextField } from '@web/atomic/legacy/atm.text-field';
import { TextFieldMasked } from '@web/atomic/legacy/atm.text-field/text-field-masked.component';
import { FlashMessage } from '@web/atomic/legacy/mol.flash-message';
import { ForcedFade } from '@web/atomic/legacy/obj.animation/animation.component.style';
import { Form, FormContext, FormContextState, FormData, Validators } from '@web/atomic/legacy/obj.form';
import { MatchMedia } from '@web/atomic/legacy/obj.match-media';
import { RelativeWrapper } from '@web/atomic/obj.wrappers';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useState } from 'react';
import {
  AnxietyTitle,
  ConcernedTitle,
  ControlConcernsTitle,
  DiscouragementTitle,
  GenderTitle,
  InitialInsomniaTitle,
  InterferenceTitle,
  MiddleInsomniaTitle,
  OthersPerceptionTitle,
  PleasureTitle,
  SatisfactionTitle,
  SleepDisturbTitle,
  SleepProblemAgeTitle,
  TerminalInsomniaTitle,
  TermTitle,
  WeekFrequencyTitle,
} from './quiz-b2b-questions';

// TODO: Specify the type of each handler ~ Why: consistency of interfaces
// TODO: Extract one component for each one of the array items ~ Why: function is too big (reponsability overload)

const timeToChange = 350;

export interface QuestionProps {
  onNext: (formData?: FormData<QuizB2BData>) => void;
  onBack: (formData?: FormData<QuizB2BData>) => void;
  viewToShow: React.FunctionComponent<QuestionProps>;
  isLastQuestion: boolean;
  prefilledValues: Partial<QuizB2BData>;
}

export const Introduction: React.FunctionComponent<QuestionProps> = (props) => {
  const { t } = useTranslation('quiz');
  const data = useStaticQuery(graphql`
    query IntroductionQuery {
      girl: file(relativePath: { eq: "girl-with-mask.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 930, layout: CONSTRAINED)
        }
      }
    }
  `);

  const { onNext } = props;
  return (
    <>
      <ForcedFade show={true}>
        <Row mt center="xs">
          <Col xs={9} sm={7} md={7} lg={6}>
            <RelativeWrapper>
              <GatsbyImage image={data.girl.childImageSharp.gatsbyImageData} alt={t('quiz_b2b_girl_with_mask_alt')} />
            </RelativeWrapper>
          </Col>
        </Row>
        <Row mb center="xs">
          <Col xs={12} sm={10}>
            <Separator />
            <H1 center>{t('quiz_b2b_sleep_score_title')}</H1>
            <Separator />
            <Body>
              {t('quiz_b2b_sleep_score_description')}{' '}
              <span role="img" aria-label={t('quiz_b2b_security_label')}>
                &#128274; &#128077;
              </span>
            </Body>
          </Col>
        </Row>
        <Row mt center="xs">
          <Col xs={12} sm={10}>
            <GatsbyButton onClick={onNext} type="button" kind="primary" expanded>
              {t('quiz_b2b_start_button')}
            </GatsbyButton>
            <Separator />
          </Col>
        </Row>
      </ForcedFade>
    </>
  );
};

export enum ConsentAnswer {
  Accepted = 'accept',
  Denied = 'deny',
  None = 'none',
}

export const ConsentTerm: React.FunctionComponent<QuestionProps> = (props) => {
  const { t } = useTranslation('quiz');
  const { onBack, onNext, prefilledValues, isLastQuestion } = props;
  const [consent, setConsent] = useState(ConsentAnswer.None);

  const onAcceptClick = (value): void => {
    setConsent(value);
    setTimeout(onNext, timeToChange);
  };

  const handleNextClick = (formData) => {
    setConsent(formData?.data?.term);
    if (consent === ConsentAnswer.Accepted) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };

  const name: keyof QuizB2BData = 'term';
  return (
    <>
      <ForcedFade show={true}>
        {consent === ConsentAnswer.Denied && (
          <Row mb center="xs">
            <FlashMessage type={'alert'} dismissible={false} autoClose={false}>
              <Body>{t('quiz_b2b_consent_denial_message')}</Body>
            </FlashMessage>
          </Row>
        )}
        <Form.Field name={name} key={prefilledValues[name]} initialValue={prefilledValues[name]} validators={[Validators.Required()]}>
          <BigInputLabel>
            <TermTitle />
          </BigInputLabel>
          <Separator />
          <RadioField onClick={onAcceptClick} key={'accept'} id={'accept'}>
            {t('quiz_b2b_accept_button')}
          </RadioField>
          <RadioField onClick={setConsent} key={'deny'} id={'deny'}>
            {t('quiz_b2b_deny_button')}
          </RadioField>
        </Form.Field>
        <Separator />
        <Separator />
        <FooterButtons formName={name} onNext={handleNextClick} onBack={onBack} isLastQuestion={isLastQuestion} />
      </ForcedFade>
    </>
  );
};

export const NoHeader: React.FunctionComponent = () => (
  <MatchMedia defaultMinWidth={`48rem`}>
    {(md): JSX.Element =>
      !md ? (
        <>
          <Separator />
          <Separator />
          <Separator />
          <Separator />
          <Separator />
          <Separator />
        </>
      ) : (
        <>
          <Separator />
          <Separator />
          <Separator />
        </>
      )
    }
  </MatchMedia>
);

export const HeaderTemplatePart: React.FunctionComponent<{ children?: React.ReactNode }> = (props) => {
  return (
    <MatchMedia defaultMinWidth={`48rem`}>
      {(md) => (md ? <H3>{props.children}</H3> : <FeaturedH2>{props.children}</FeaturedH2>)}
    </MatchMedia>
  );
};

export const HeaderFirstPart: React.FunctionComponent = () => {
  const { t } = useTranslation('quiz');
  return (
    <HeaderTemplatePart>
      <span role="img" aria-label={t('quiz_b2b_instructions_label')}>
        &#9888;&#65039;
      </span>{' '}
      {t('quiz_b2b_header_first_part')}
    </HeaderTemplatePart>
  );
};

export const HeaderSecondPart: React.FunctionComponent = () => {
  const { t } = useTranslation('quiz');
  return (
    <HeaderTemplatePart>
      <span role="img" aria-label={t('quiz_b2b_instructions_label')}>
        &#9888;&#65039;
      </span>{' '}
      {t('quiz_b2b_header_second_part')}
    </HeaderTemplatePart>
  );
};

export const QuestionName: React.FunctionComponent<QuestionProps> = (props) => {
  const { t } = useTranslation('quiz');
  const { onNext, onBack, isLastQuestion, prefilledValues } = props;

  const onNextClick = (formData) => {
    if (!formData.error.name) {
      setTimeout(() => onNext(formData), 100);
    }
  };

  const name: keyof QuizB2BData = 'name';

  return (
    <>
      <NoHeader />
      <ForcedFade show={true}>
        <Form.Field key={prefilledValues[name]} initialValue={prefilledValues[name]} name={name} validators={[Validators.Required()]}>
          <BigInputLabel>{t('quiz_b2b_name_question')}</BigInputLabel>
          <TextField type={'text'} />
          <Separator />
        </Form.Field>
        <Separator />
        <FooterButtons formName={name} onNext={onNextClick} onBack={onBack} isLastQuestion={isLastQuestion} />
      </ForcedFade>
    </>
  );
};

export const CompanyQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { t } = useTranslation('quiz');
  const { onNext, onBack, isLastQuestion, prefilledValues } = props;

  const onNextClick = (formData) => {
    if (!formData.error['company']) {
      setTimeout(() => onNext(formData), 100);
    }
  };

  const name: keyof QuizB2BData = 'company';

  return (
    <>
      <NoHeader />
      <ForcedFade show={true}>
        <Form.Field name={name} key={prefilledValues[name]} initialValue={prefilledValues[name]} validators={[Validators.Required()]}>
          <BigInputLabel>
            {t('quiz_b2b_company_question')}{' '}
            <span role="img" aria-label={t('quiz_b2b_company_label')}>
              &#x1F913;
            </span>
          </BigInputLabel>
          <TextField type={'text'} />
          <Separator />
        </Form.Field>
        <Separator />
        <FooterButtons formName={name} onNext={onNextClick} onBack={onBack} isLastQuestion={isLastQuestion} />
      </ForcedFade>
    </>
  );
};

export const BirthdateQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { t } = useTranslation('quiz');
  const { onNext, onBack, isLastQuestion, prefilledValues } = props;

  const name: keyof QuizB2BData = 'birthdate';

  return (
    <>
      <NoHeader />
      <ForcedFade show={true}>
        <Form.Field name={name} key={prefilledValues[name]} initialValue={prefilledValues[name]} validators={[Validators.Required()]}>
          <BigInputLabel>{t('quiz_b2b_birthdate_question')}</BigInputLabel>
          <TextFieldMasked kind={'datetime'} placeholder={t('quiz_b2b_birthdate_placeholder')} options={{ format: 'DD/MM/YYYY' }} />
          <Separator />
        </Form.Field>
        <Separator />
        <FooterButtons formName={name} onNext={onNext} onBack={onBack} isLastQuestion={isLastQuestion} />
      </ForcedFade>
    </>
  );
};

export const CPFQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { t } = useTranslation('quiz');
  const { onNext, onBack, isLastQuestion, prefilledValues } = props;

  const onNextClick = (formData) => {
    if (!formData.error['cpf']) {
      setTimeout(() => onNext(formData), 100);
    }
  };

  const name: keyof QuizB2BData = 'cpf';

  return (
    <>
      <NoHeader />
      <ForcedFade show={true}>
        <Form.Field
          name={name}
          key={prefilledValues[name]}
          initialValue={prefilledValues[name]}
          validators={[Validators.Required(), Validators.CpfRegex(t('quiz_b2b_cpf_invalid'))]}
        >
          <BigInputLabel>{t('quiz_b2b_cpf_question')}</BigInputLabel>
          <TextFieldMasked kind={'cpf'} placeholder={t('quiz_b2b_cpf_placeholder')} />
          <Separator />
        </Form.Field>
        <Separator />
        <FooterButtons formName={name} onNext={onNextClick} onBack={onBack} isLastQuestion={isLastQuestion} />
      </ForcedFade>
    </>
  );
};

export const InitialInsomniaQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['initialInsomnia']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required
        questionTitle={InitialInsomniaTitle}
        radioValues={InitialInsomnia}
        formValueName="initialInsomnia"
        onBackClick={onBack}
        onNextClick={onNextClick}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const MiddleInsomniaQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['middleInsomnia']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required
        questionTitle={MiddleInsomniaTitle}
        radioValues={MiddleInsomnia}
        formValueName="middleInsomnia"
        onBackClick={onBack}
        onNextClick={onNextClick}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const TerminalInsomniaQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['terminalInsomnia']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required
        questionTitle={TerminalInsomniaTitle}
        radioValues={TerminalInsomnia}
        formValueName="terminalInsomnia"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const SatisfactionQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['satisfaction']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required
        questionTitle={SatisfactionTitle}
        radioValues={Satisfaction}
        formValueName="satisfaction"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const InterferenceQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['interference']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required
        questionTitle={InterferenceTitle}
        radioValues={Interference}
        formValueName="interference"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const OthersPerceptionQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['othersPerception']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required
        questionTitle={OthersPerceptionTitle}
        radioValues={OthersPerception}
        formValueName="othersPerception"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const ConcernedQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['concerned']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required
        questionTitle={ConcernedTitle}
        radioValues={Concerned}
        formValueName="concerned"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const SleepProblemAgeQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['sleepProblemAge']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required={false}
        questionTitle={SleepProblemAgeTitle}
        radioValues={SleepProblemAge}
        formValueName="sleepProblemAge"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const WeekFrequencyQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['weekFrequency']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required={false}
        questionTitle={WeekFrequencyTitle}
        radioValues={WeekFrequency}
        formValueName="weekFrequency"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const SleepDisturbQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['sleepDisturb']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderFirstPart />
      <RadioButtonQuestion
        required
        questionTitle={SleepDisturbTitle}
        radioValues={SleepDisturb}
        formValueName="sleepDisturb"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};
export const PleasureQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['pleasure']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderSecondPart />
      <RadioButtonQuestion
        required
        questionTitle={PleasureTitle}
        radioValues={Pleasure}
        formValueName="pleasure"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const DiscouragementQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['discouragement']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderSecondPart />
      <RadioButtonQuestion
        required
        questionTitle={DiscouragementTitle}
        radioValues={Discouragement}
        formValueName="discouragement"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const AnxietyQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['anxiety']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderSecondPart />
      <RadioButtonQuestion
        required
        questionTitle={AnxietyTitle}
        radioValues={Anxiety}
        formValueName="anxiety"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const ControlConcernsQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['controlConcerns']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <HeaderSecondPart />
      <RadioButtonQuestion
        required
        questionTitle={ControlConcernsTitle}
        radioValues={ControlConcerns}
        formValueName="controlConcerns"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

export const GenderQuestion: React.FunctionComponent<QuestionProps> = (props) => {
  const { onNext, onBack, viewToShow, isLastQuestion, prefilledValues } = props;
  const onNextClick = (formData) => {
    if (!formData.error['gender']) {
      setTimeout(() => onNext(formData), timeToChange);
    }
  };
  return (
    <>
      <NoHeader />
      <RadioButtonQuestion
        required
        questionTitle={GenderTitle}
        radioValues={Gender}
        formValueName="gender"
        onNextClick={onNextClick}
        onBackClick={onBack}
        viewToShow={viewToShow}
        isLastQuestion={isLastQuestion}
        prefilledValues={prefilledValues}
      />
    </>
  );
};

////////////////////////////////////////////////////////////////////////////////////////////////////

interface IFooterButtonsProps {
  formName: string;
  onNext?: (formData?: FormData<QuizB2BData>) => void;
  onBack: (formData?: FormData<QuizB2BData>) => void;
  isLastQuestion: boolean;
}

const FooterButtons: React.FunctionComponent<IFooterButtonsProps> = (props) => {
  const { t } = useTranslation('quiz');
  return (
    <FormContext.Consumer>
      {(formConsumer: FormContextState) => {
        return (
          <Row between="xs">
            <Col>
              <GatsbyButton onClick={props.onBack} type="button" kind="secondary">
                <FaIcon.Back /> &nbsp; {t('quiz_b2b_back_button')}
              </GatsbyButton>
            </Col>
            {props.isLastQuestion && props.onNext && (
              <Col>
                <GatsbyButton
                  type="submit"
                  kind="call-to-action"
                  onClick={async () => {
                    const formData = await formConsumer.validateField(props.formName);
                    props.onNext(formData);
                  }}
                >
                  <MatchMedia defaultMinWidth={`340px`}>
                    {(md) => (md ? t('quiz_b2b_submit_button') : t('quiz_b2b_see_result_button'))}
                  </MatchMedia>
                </GatsbyButton>
              </Col>
            )}
            {!props.isLastQuestion && props.onNext && (
              <Col>
                <GatsbyButton
                  onClick={async () => {
                    const formData = await formConsumer.validateField(props.formName);
                    props.onNext(formData);
                  }}
                  type="button"
                  kind="primary"
                >
                  {t('quiz_b2b_next_button')}
                </GatsbyButton>
              </Col>
            )}
          </Row>
        );
      }}
    </FormContext.Consumer>
  );
};

////////////////////////////////////////////////////////////////////////////////////////////////////

interface RadioButtonQuestionProps {
  questionTitle: React.FunctionComponent;
  radioValues: any;
  formValueName: keyof QuizB2BData;
  onNextClick: (formData?: FormData<QuizB2BData>) => void;
  onBackClick: (formData?: FormData<QuizB2BData>) => void;
  viewToShow: React.FunctionComponent<QuestionProps>;
  required: boolean;
  isLastQuestion: boolean;
  prefilledValues: Partial<QuizB2BData>;
}

const RadioButtonQuestion: React.FunctionComponent<RadioButtonQuestionProps> = (props) => {
  const { t } = useTranslation('quiz');
  const { onBackClick, onNextClick, questionTitle, radioValues, formValueName, required, isLastQuestion, prefilledValues } = props;

  const validators = required ? [Validators.Required()] : [];
  return (
    <>
      <ForcedFade show={true}>
        <FormContext.Consumer>
          {(formConsumer: FormContextState) => {
            return (
              <Form.Field
                name={formValueName}
                key={prefilledValues[formValueName] as string}
                initialValue={prefilledValues[formValueName]}
                validators={validators}
                onValueChange={async (v) => {
                  const formData = await formConsumer.getFormData();
                  onNextClick(formData);
                }}
              >
                <>
                  <BigInputLabel>{questionTitle({})}</BigInputLabel>
                  <Separator />
                  {Object.values(radioValues).map((value: string) => (
                    <RadioField key={value} id={value}>
                      {value}
                    </RadioField>
                  ))}
                  <Separator />
                  <Separator />
                  <Row between="xs">
                    <Col>
                      <GatsbyButton
                        onClick={async () => {
                          const formData = await formConsumer.getFormData();
                          onBackClick(formData);
                        }}
                        type="button"
                        kind="secondary"
                      >
                        <FaIcon.Back /> &nbsp; {t('quiz_b2b_back_button')}
                      </GatsbyButton>
                      <Separator />
                    </Col>
                    {isLastQuestion && (
                      <Col>
                        <GatsbyButton type="submit" kind="call-to-action">
                          <MatchMedia defaultMinWidth={`340px`}>
                            {(md) => (md ? t('quiz_b2b_submit_button') : t('quiz_b2b_see_result_button'))}
                          </MatchMedia>
                        </GatsbyButton>
                      </Col>
                    )}
                  </Row>
                </>
              </Form.Field>
            );
          }}
        </FormContext.Consumer>
      </ForcedFade>
    </>
  );
};
